import { useEffect, useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { Search, User as UserIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { adminOrderApi, type CrmebUser } from '@/api/admin-order'
import { useOrderCreateStore } from '@/stores/order-create'

const phonePattern = /^1[3-9]\d{9}$/

export function UserStep() {
  const { selectedUser, setSelectedUser, nextStep } = useOrderCreateStore()
  const [searchParams] = useSearchParams()
  const [keyword, setKeyword] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [searchError, setSearchError] = useState('')
  const uidParam = searchParams.get('uid')
  const uidFromUrl = uidParam ? Number(uidParam) : 0

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'orders', 'users', searchTerm],
    queryFn: () => adminOrderApi.searchUsers(searchTerm),
    enabled: searchTerm.length > 0,
  })

  const { data: preselectedUser } = useQuery({
    queryKey: ['admin', 'orders', 'users', uidFromUrl],
    queryFn: () => adminOrderApi.getUser(uidFromUrl),
    enabled: !selectedUser && uidFromUrl > 0,
  })

  useEffect(() => {
    if (!selectedUser && preselectedUser) {
      setSelectedUser(preselectedUser)
    }
  }, [preselectedUser, selectedUser, setSelectedUser])

  const handleSearch = useCallback(() => {
    const phone = keyword.trim()
    if (!phonePattern.test(phone)) {
      setSearchError('只能使用手机号搜索客户')
      setSearchTerm('')
      return
    }
    setSearchError('')
    setSearchTerm(phone)
  }, [keyword])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }, [handleSearch])

  const handleSelectUser = useCallback((user: CrmebUser) => {
    setSelectedUser(user)
  }, [setSelectedUser])

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="请输入客户手机号"
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value)
              if (searchError) setSearchError('')
            }}
            onKeyDown={handleKeyDown}
            className="pl-9"
          />
        </div>
        <Button onClick={handleSearch} disabled={keyword.trim().length === 0}>
          搜索
        </Button>
      </div>
      {searchError && (
        <p className="text-sm text-destructive">{searchError}</p>
      )}

      {/* 搜索结果表格 */}
      {searchTerm && (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>昵称</TableHead>
                <TableHead>手机号</TableHead>
                <TableHead>余额</TableHead>
                <TableHead>积分</TableHead>
                <TableHead>状态</TableHead>
                <TableHead className="w-20">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    加载中...
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && data?.list?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    未找到匹配的用户
                  </TableCell>
                </TableRow>
              )}
              {data?.list?.map((user) => (
                <TableRow
                  key={user.uid}
                  className={selectedUser?.uid === user.uid ? 'bg-primary/5' : ''}
                >
                  <TableCell>
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user.avatar} />
                      <AvatarFallback><UserIcon className="h-4 w-4" /></AvatarFallback>
                    </Avatar>
                  </TableCell>
                  <TableCell className="font-medium">{user.nickname}</TableCell>
                  <TableCell>{user.phone || '-'}</TableCell>
                  <TableCell>{user.now_money}</TableCell>
                  <TableCell>{user.integral}</TableCell>
                  <TableCell>
                    <Badge variant={user.status === 1 ? 'default' : 'secondary'}>
                      {user.status === 1 ? '正常' : '禁用'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant={selectedUser?.uid === user.uid ? 'default' : 'outline'}
                      onClick={() => handleSelectUser(user)}
                    >
                      {selectedUser?.uid === user.uid ? '已选择' : '选择'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* 已选用户卡片 */}
      {selectedUser && (
        <Card className="border-primary">
          <CardContent className="flex items-center gap-4 py-4">
            <Avatar className="h-12 w-12">
              <AvatarImage src={selectedUser.avatar} />
              <AvatarFallback><UserIcon className="h-6 w-6" /></AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="font-medium">{selectedUser.nickname}</p>
              <p className="text-sm text-muted-foreground">
                手机号: {selectedUser.phone || '-'} | 余额: {selectedUser.now_money} | 积分: {selectedUser.integral}
              </p>
            </div>
            <Button onClick={nextStep}>
              下一步
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
